import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { HOST } from 'config/config';
import tableDataQueryStringBuilder from 'services/Utilities/TableData';

/**
 * Fetch all submissions with TableData filtering, sorting, and pagination
 * @param {Object} tableDataParams - TableData parameters object
 */
export const useSubmissionsTableData = tableDataParams => {
  const queryString = tableDataQueryStringBuilder(tableDataParams);

  return useQuery({
    queryKey: ['submissions', 'tableData', queryString],
    queryFn: async () => {
      const response = await axios.get(`${HOST}/api/certification-tool/submissions?${queryString}`);
      return response.data;
    },
    staleTime: 0, // No caching - always fetch fresh
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    enabled: !!tableDataParams,
  });
};

/**
 * Fetch all submissions with optional pagination
 * @param {Object} tableData - Optional pagination parameters
 */
export const useSubmissions = (tableData = null) => {
  return useQuery({
    queryKey: ['submissions', tableData],
    queryFn: async () => {
      const params = tableData ? { ...tableData } : {};
      const response = await axios.get(`${HOST}/api/certification-tool/submissions`, { params });
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Fetch submissions by dealer ID
 * @param {string} dealerId - The dealer ID
 */
export const useSubmissionsByDealer = dealerId => {
  return useQuery({
    queryKey: ['submissions', 'dealer', dealerId],
    queryFn: async () => {
      if (!dealerId) {
        throw new Error('Dealer ID is required');
      }
      const response = await axios.get(`${HOST}/api/certification-tool/submissions/dealer/${dealerId}`);
      return response.data;
    },
    enabled: !!dealerId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Fetch a single submission by ID
 * @param {string} submissionId - The submission ID
 */
export const useSubmission = submissionId => {
  return useQuery({
    queryKey: ['submission', submissionId],
    queryFn: async () => {
      if (!submissionId) {
        throw new Error('Submission ID is required');
      }
      const response = await axios.get(`${HOST}/api/certification-tool/submissions/${submissionId}`);
      return response.data;
    },
    enabled: !!submissionId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Create a new submission
 */
export const useCreateSubmission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async submissionData => {
      const response = await axios.post(`${HOST}/api/certification-tool/submissions`, submissionData);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch submissions queries
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
    },
  });
};

/**
 * Update an existing submission using PATCH
 * @param {string} submissionId - The submission ID
 */
export const useUpdateSubmission = submissionId => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async patchDocument => {
      const response = await axios.patch(`${HOST}/api/certification-tool/submissions/${submissionId}`, patchDocument);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['submission', submissionId] });
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
    },
  });
};

/**
 * Delete a submission
 * @param {string} submissionId - The submission ID
 */
export const useDeleteSubmission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async submissionId => {
      await axios.delete(`${HOST}/api/certification-tool/submissions/${submissionId}`);
      return submissionId;
    },
    onSuccess: () => {
      // Invalidate and refetch submissions queries
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
    },
  });
};
